import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {EmailService} from '../../services/api/administration/email.service';
import {SimpleFormGroup} from '@browsepedia/ng-forms';
import {Backend} from '@generated-backend-types';
import {FormArray, FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgForOf} from '@angular/common';
import {FormGroupComponent} from '../form-inputs/form-group/form-group.component';
import {MarkdownEditorComponent} from '../form-inputs/markdown-editor/markdown-editor.component';
import {
  MultiSelectWithServiceComponent
} from '../form-inputs/multi-select-with-service/multi-select-with-service.component';
import {debounceTime, Subject, takeUntil} from 'rxjs';
import {EmailPreviewComponent} from '../email-preview/email-preview.component';
import {ToastrService} from 'ngx-toastr';
import PatientEmailData = Backend.App.Data.Dashboard.Administration.PatientEmailData;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;
import Patient = Backend.App.Resources.Dashboard.Patient;
import Email = Backend.App.Resources.Dashboard.Administration.Email;
import PatientLanguageEnum = Backend.App.Enums.PatientLanguageEnum;
import EmailPreviewData = Backend.App.Data.Dashboard.Administration.EmailPreviewData;
import EmailData = Backend.App.Data.Dashboard.Administration.EmailData;
import MultilingualAttributeData = Backend.App.Data.Meta.MultilingualAttributeData;
import ReducedUser = Backend.App.Resources.Dashboard.Administration.ReducedUser;

@Component({
  selector: 'app-email-send',
  templateUrl: './email-send.component.html',
  providers: [BsModalService],
  imports: [ModalModule, TranslatePipe, ReactiveFormsModule, NgForOf, FormGroupComponent, MarkdownEditorComponent, MultiSelectWithServiceComponent, EmailPreviewComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true
})
export class EmailSendComponent implements OnInit, OnDestroy {
  @Input({required: true}) public recipient!: Patient | ReducedUser;
  @Input({required: true}) public type!: EmailTypeEnum;
  @Input() public sendEmail = true;
  /** When the parent handles the actual submission (sendEmail=false), it binds
   *  its pending state here so the submit button stays locked during the request. */
  @Input() public pending = false;
  @Output() public closeEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() public sendEmailEvent: EventEmitter<PatientEmailData> = new EventEmitter<PatientEmailData>();

  protected emailTemplateControl = this.fb.control<Email[]>([], [Validators.required]);

  protected email?: Email;
  protected emailPreviewData$ = new Subject<EmailPreviewData>();

  protected reloadTemplatesEmitter = new EventEmitter<void>;

  protected form: SimpleFormGroup<PatientEmailData> = this.fb.group({
    subject: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(1)]),
    pre_content: this.fb.control('', [Validators.required]),
    post_content: this.fb.control('', [Validators.required]),
    cc_recipients: this.fb.array<string[]>([], {updateOn: 'change'}),
    bcc_recipients: this.fb.array<string[]>([], {updateOn: 'change'}),
    email_id: this.fb.control('', [Validators.required])
  }) as unknown as SimpleFormGroup<PatientEmailData>;

  private onDestroy$: Subject<void> = new Subject<void>();

  public constructor(
    private readonly cdr: ChangeDetectorRef,
    protected readonly emailService: EmailService,
    private readonly fb: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly translate: TranslateService,
  ) {}

  public get canNotSubmit() {
    return !this.email ||
      this.pending ||
      this.form.controls.cc_recipients.invalid ||
      this.form.controls.bcc_recipients.invalid ||
      this.form.controls.subject.invalid ||
      this.form.controls.pre_content.invalid ||
      this.form.controls.post_content.invalid;
  }

  private get recipientLanguage(): PatientLanguageEnum {
    if ('language' in this.recipient) {
      return this.recipient.language;
    }

    return PatientLanguageEnum.GERMAN
  }

  private get recipientLang(): 'de' | 'en' | 'ru' {
    switch (this.recipientLanguage) {
      case PatientLanguageEnum.GERMAN:
        return 'de';
      case PatientLanguageEnum.ENGLISH:
        return 'en';
      case PatientLanguageEnum.RUSSIAN:
        return 'ru';
    }
  }

  public ngOnInit() {
    this.emailTemplateControl.valueChanges.pipe(takeUntil(this.onDestroy$), debounceTime(100))
      .subscribe(val => {
        this.form.controls.subject.setValue(null);
        this.form.controls.pre_content.setValue(null);
        this.form.controls.post_content.setValue(null);

        if (val === null || (val?.length ?? 0) <= 0) {
          this.email = undefined;
          this.cdr.markForCheck();
          return;
        }

        const email = val[0];
        this.email = email;

        this.form.controls.subject.setValue(email.subject[this.recipientLang] ?? '');
        this.form.controls.pre_content.setValue(email.pre_content[this.recipientLang] ?? '');
        this.form.controls.post_content.setValue(email.post_content[this.recipientLang] ?? '');

        this.cdr.markForCheck();
      })
  }

  public ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public submit(): void {
    this.form.markAllAsTouched();
    this.form.markAllAsDirty();

    if (this.canNotSubmit) return;

    const data = this.form.getRawValue();
    data.pre_content = this.prepareMarkdownForBackend(data.pre_content ?? '');
    data.post_content = this.prepareMarkdownForBackend(data.post_content ?? '');
    data.email_id = this.email!.id;

    if (this.sendEmail) {
      this.pending = true;
      this.emailService.sendPatientEmail(this.recipient.id, data).pipe(takeUntil(this.onDestroy$)).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('components.email_send.success'));
          this.sendEmailEvent.emit(data);
          this.closeEvent.emit();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('components.email_send.error'));
          this.pending = false;
          this.cdr.markForCheck();
        },
      });

      return;
    }

    // Parent handles the actual submission and closes the modal on success.
    this.sendEmailEvent.emit(data);
  }

  public removeRecipient(recipientType: 'cc' | 'bcc', index: number): void {
    const formControls = (recipientType === 'bcc' ? this.form.controls.bcc_recipients : this.form.controls.cc_recipients) as unknown as FormArray;
    formControls.removeAt(index);
    this.cdr.markForCheck();
  }

  public addRecipient(recipientType: 'cc' | 'bcc'): void {
    const formControls = (recipientType === 'bcc' ? this.form.controls.bcc_recipients : this.form.controls.cc_recipients) as unknown as FormArray;
    formControls.push(this.fb.control('', [Validators.email, Validators.required]));
    this.cdr.markForCheck();
  }

  public showPreview() {
    if (this.email) {
      this.emailPreviewData$.next(this.getEmailPreviewData());
    }
  }

  private getEmailPreviewData(): EmailPreviewData {
    const emailData = this.email! as EmailData;

    const pre_content: MultilingualAttributeData = {
      de: emailData.pre_content?.de ?? '',
      en: emailData.pre_content?.en ?? '',
      ru: emailData.pre_content?.ru ?? '',
    }

    const post_content: MultilingualAttributeData = {
      de: emailData.post_content?.de ?? '',
      en: emailData.post_content?.en ?? '',
      ru: emailData.post_content?.ru ?? '',
    }

    pre_content[this.recipientLang] = this.prepareMarkdownForBackend(this.form.controls.pre_content.value ?? '');
    post_content[this.recipientLang] = this.prepareMarkdownForBackend(this.form.controls.post_content.value ?? '');

    return {
      language: this.recipientLanguage,
      content: this.email!.content,
      pre_content,
      post_content,
      patient_id: this.recipient.id,
    };
  }

  private prepareMarkdownForBackend(markdown: string): string {
    return markdown.replace(/{{--(.*?)--}}/g, (match, comment) => {
      // Remove backslashes within the comment
      const cleanedComment = comment.replace(/\\/g, '');
      return `{{--${cleanedComment}--}}`;
    });
  }
}
